import { Accordion, Alert, Badge, Box, Card, Grid, Group, Stack, Text, Title } from "@mantine/core"
import { IconAlertTriangle, IconCheck, IconClock, IconServer, IconX } from "@tabler/icons-react"

import { MONITORED_SERVICES, type ServiceId } from "@/config/services"
import type { MultiServiceHealthData } from "@/hooks/useMultiServiceHealth"

interface HealthOverviewProps {
  healthData: MultiServiceHealthData
}

export function HealthOverview({ healthData }: HealthOverviewProps) {
  const getStatusColor = (status: "healthy" | "degraded" | "unhealthy") => {
    switch (status) {
      case "healthy":
        return "green"
      case "degraded":
        return "yellow"
      case "unhealthy":
        return "red"
    }
  }

  const getStatusIcon = (status: "healthy" | "degraded" | "unhealthy") => {
    switch (status) {
      case "healthy":
        return <IconCheck size={20} />
      case "degraded":
        return <IconAlertTriangle size={20} />
      case "unhealthy":
        return <IconX size={20} />
    }
  }

  const getServiceStatusIcon = (status: "ok" | "warning" | "error") => {
    switch (status) {
      case "ok":
        return <IconCheck size={16} />
      case "warning":
        return <IconAlertTriangle size={16} />
      case "error":
        return <IconX size={16} />
    }
  }

  const getServiceStatusColor = (status: "ok" | "warning" | "error") => {
    switch (status) {
      case "ok":
        return "green"
      case "warning":
        return "yellow"
      case "error":
        return "red"
    }
  }

  // Calculate service counts
  const serviceCount = Object.keys(healthData.services).length
  const healthyCount = Object.values(healthData.services).filter((s) => s.healthData?.overall === "healthy").length
  const onlineCount = Object.values(healthData.services).filter((s) => s.healthData !== null).length

  return (
    <Stack gap="lg">
      {/* Overall System Status */}
      <Card shadow="sm" padding="xl" radius="md">
        <Group justify="space-between" mb="md">
          <Box>
            <Group>
              {getStatusIcon(healthData.overallStatus)}
              <Title order={2}>Platform Status</Title>
            </Group>
            <Text size="lg" mt="xs">
              All services are {healthData.overallStatus}
            </Text>
          </Box>
          <Badge
            size="xl"
            color={getStatusColor(healthData.overallStatus)}
            leftSection={getStatusIcon(healthData.overallStatus)}
          >
            {healthData.overallStatus.toUpperCase()}
          </Badge>
        </Group>

        <Group gap="xl">
          <Group gap="xs">
            <IconServer size={16} />
            <Text size="sm" fw={500}>
              {onlineCount}/{serviceCount} services online
            </Text>
          </Group>
          <Group gap="xs">
            <IconCheck size={16} color="green" />
            <Text size="sm" fw={500}>
              {healthyCount}/{serviceCount} healthy
            </Text>
          </Group>
        </Group>
      </Card>

      {/* Individual Service Status Cards */}
      <Grid>
        {(Object.keys(healthData.services) as ServiceId[]).map((serviceId) => {
          const service = healthData.services[serviceId]
          const serviceConfig = MONITORED_SERVICES[serviceId]

          // Determine service status
          const serviceStatus = service.error
            ? "unhealthy"
            : service.healthData
              ? service.healthData.overall
              : "unhealthy"

          const lastUpdated = service.healthData?.timestamp
            ? new Date(service.healthData.timestamp).toLocaleString()
            : "Unknown"

          return (
            <Grid.Col key={serviceId} span={{ base: 12, md: 6 }}>
              <Card
                shadow="sm"
                padding="lg"
                radius="md"
                style={{
                  borderLeft: `4px solid var(--mantine-color-${getStatusColor(serviceStatus)}-6)`,
                }}
              >
                {/* Service Header */}
                <Group justify="space-between" mb="md">
                  <Box style={{ flex: 1 }}>
                    <Group gap="xs" mb={4}>
                      <Text fw={700} size="lg">
                        {serviceConfig.name}
                      </Text>
                      <Badge
                        color={getStatusColor(serviceStatus)}
                        leftSection={getStatusIcon(serviceStatus)}
                        size="lg"
                        variant="filled"
                      >
                        {serviceStatus === "unhealthy" && !service.healthData ? "OFFLINE" : serviceStatus.toUpperCase()}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {serviceConfig.description}
                    </Text>
                  </Box>
                </Group>

                {/* Service URL */}
                <Group gap="xs" mb="md">
                  <IconServer size={14} />
                  <Text size="xs" c="dimmed" style={{ fontFamily: "monospace" }}>
                    {service.url}/health/detailed
                  </Text>
                </Group>

                {/* Error Alert */}
                {service.error && (
                  <Alert color="red" icon={<IconX size={16} />} mb="md">
                    <Text size="sm" fw={500} mb={4}>
                      Service Unavailable
                    </Text>
                    <Text size="xs">{service.error.message}</Text>
                    {service.error.message.includes("503") && (
                      <Text size="xs" c="dimmed" mt={4}>
                        The service may be experiencing startup issues or configuration problems.
                      </Text>
                    )}
                    {service.error.message.includes("404") && (
                      <Text size="xs" c="dimmed" mt={4}>
                        The health endpoint may not be deployed or configured correctly.
                      </Text>
                    )}
                  </Alert>
                )}

                {/* Service Health Checks */}
                {service.healthData && Object.keys(service.healthData.services).length > 0 && (
                  <Accordion variant="separated" radius="sm">
                    <Accordion.Item value="details">
                      <Accordion.Control>
                        <Text size="sm" fw={500}>
                          Health Checks ({Object.keys(service.healthData.services).length})
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="xs">
                          {Object.entries(service.healthData.services).map(([checkName, checkResult]) => (
                            <Group key={checkName} justify="space-between">
                              <Text size="sm" tt="capitalize">
                                {checkName.replace(/([A-Z])/g, " $1").trim()}
                              </Text>
                              <Badge
                                size="sm"
                                color={getServiceStatusColor(checkResult.status)}
                                leftSection={getServiceStatusIcon(checkResult.status)}
                              >
                                {checkResult.status.toUpperCase()}
                              </Badge>
                            </Group>
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )}

                {/* Last Updated */}
                <Group mt="md" gap="xs">
                  <IconClock size={14} />
                  <Text size="xs" c="dimmed">
                    Last check: {lastUpdated}
                  </Text>
                </Group>
              </Card>
            </Grid.Col>
          )
        })}
      </Grid>
    </Stack>
  )
}
